import { useEffect, useRef, useState } from "react";

export type Alert = {
  id: string;
  summary: string;
  description?: string;
  url?: string;
  severity?: string;
  reason?: string;
  affected_routes?: string[];
  active_from?: number;
  active_to?: number;
  created_at?: number;
};

export type AlertsSnapshot = {
  alerts: Alert[];
  generated: number;
};

// useAlerts polls /api/realtime/alerts on a slow loop. Alerts don't change
// minute-to-minute, so 60s is plenty; we also re-fetch on tab focus so
// returning to the tab after a long absence doesn't show stale data.
export function useAlerts(intervalMs = 60_000): {
  alerts: Alert[];
  loading: boolean;
} {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const fetchOnce = async () => {
      try {
        const res = await fetch("/api/realtime/alerts");
        if (!res.ok) {
          // 503 just means the first poll hasn't completed yet — try again.
          return;
        }
        const data = (await res.json()) as AlertsSnapshot;
        if (cancelledRef.current) return;
        setAlerts(data.alerts ?? []);
      } catch {
        // Swallow transient network errors; the next tick will retry.
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, intervalMs);
    const onVis = () => {
      if (document.visibilityState === "visible") fetchOnce();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs]);

  return { alerts, loading };
}
